import styled from '@emotion/styled';
import { Button, Field, IconButton, Select, SessionActions, StatusPill, Stepper, CheckIcon, PlusIcon, colors, fonts, breakpoints } from '../../../ui';
import { Muted, Headline, FieldRow, filledField } from './shared';
import { CreditCard } from './CreditCard';

// Presentational patient calibration screens (Figma P3-P6). They render inside the panel of a
// SessionLayout (see PatientCalibrationScreens.js for the containers that own the ws messages).

const phone = `@media (max-width: ${breakpoints.phone - 1}px)`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  width: 100%;
  min-height: 100%;
  font-family: ${fonts.family};
  ${phone} {
    gap: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ center }) => (center ? 'center' : 'flex-start')};
  gap: 20px;
  width: 100%;
  text-align: center;
`;

// Stepper on top, content under it (every patient calibration frame starts with the stepper).
export const PatientStep = ({ current = 0, done, children, center }) => (
  <Top>
    <Stepper current={current} done={done} />
    <Body center={center}>{children}</Body>
  </Top>
);

// ---------------------------------------------------------------------------------------------
// P3  "Your clinician will now select the calibration method"
// ---------------------------------------------------------------------------------------------
export const AwaitMethodView = () => (
  <PatientStep current={0}>
    <Headline style={{ marginTop: 12 }}>Your clinician will now select the calibration method</Headline>
    <Muted small max={460} style={{ marginTop: 24 }}>
      As you await the clinician’s calibration method choice, precision is at the forefront of your remote vision test.
    </Muted>
    <Muted small max={460}>
      The selected method will ensure accurate measurements for a comprehensive and reliable assessment of your vision. Your active participation in this step ensures the best possible outcome in your eye care journey. Thank you for your patience.
    </Muted>
  </PatientStep>
);

// ---------------------------------------------------------------------------------------------
// P3.1  Make / Device selection
// ---------------------------------------------------------------------------------------------
const SelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 560px;
  text-align: left;
  ${phone} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const DeviceSelectView = ({ makes = [], models = [], make, model, onMake, onModel, onSubmit, submitted, canSubmit, loading }) => (
  <PatientStep current={0}>
    <Headline>Please select an option to measure your screen size or select your device from the list.</Headline>
    <SelectGrid>
      <Select label='Make' value={make || ''} onChange={onMake} options={makes} name='select-device-make' data-testid='select-make' />
      <Select
        label='Device'
        value={model || ''}
        onChange={onModel}
        options={models.map((m) => ({ value: m.name, label: m.name }))}
        disabled={!make || loading}
        placeholder={loading ? 'Loading…' : 'Select one…'}
        name='select-device-model'
        data-testid='select-model'
      />
    </SelectGrid>
    {submitted ? (
      <StatusPill tone='success' data-testid='device-submitted'>
        <CheckIcon size={14} strokeWidth={3} /> Device sent to your clinician. Please wait for the next step.
      </StatusPill>
    ) : (
      <Muted small>If your device is not listed, tell your clinician. They can ask you to measure your screen with a card or a ruler instead.</Muted>
    )}
    <SessionActions primary={{ label: 'Submit', onClick: onSubmit, disabled: !canSubmit }} />
  </PatientStep>
);

// ---------------------------------------------------------------------------------------------
// P3.2  Card instructions -> card measurement
// ---------------------------------------------------------------------------------------------
const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
  color: ${colors.textMuted};
  font-size: 14px;
  line-height: 1.9;
  li {
    counter-increment: step;
  }
  li::before {
    content: counter(step) '. ';
  }
`;

const Measure = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
  overflow: visible;
`;

const MinusIcon = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
    <path d='M5 12h14' />
  </svg>
);

export const CARD_STEPS = ['Put your phone on a flat surface.', 'Place your card in the box.', 'Adjust the box to touch the edges of your card.', 'Tap ‘Submit’ when you are ready to continue.'];

export const CardInstructionsView = ({ onBegin }) => (
  <PatientStep current={0}>
    <Headline>Card Measurement Instructions</Headline>
    <Steps>
      {CARD_STEPS.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </Steps>
    <Button size='lg' onClick={onBegin} style={{ marginTop: 12 }} data-testid='begin-card'>
      Begin Card Calibration Process
    </Button>
  </PatientStep>
);

export const CardMeasureView = ({ width, height, onDecrease, onIncrease, onSubmit, submitted }) => (
  <PatientStep current={0}>
    <Headline>Card Measurement Instructions</Headline>
    <Steps>
      {CARD_STEPS.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </Steps>
    <Measure>
      <IconButton label='Make the card smaller' variant='outline' size={48} onClick={onDecrease} data-testid='card-smaller'>
        <MinusIcon size={20} />
      </IconButton>
      <CreditCard width={width} height={height} submitted={submitted} />
      <IconButton label='Make the card bigger' variant='teal' size={48} onClick={onIncrease} data-testid='card-bigger'>
        <PlusIcon size={20} />
      </IconButton>
    </Measure>
    <Button size='lg' onClick={onSubmit} data-testid='card-submit'>
      Submit
    </Button>
    {submitted && (
      <StatusPill tone='success'>
        <CheckIcon size={14} strokeWidth={3} /> Card size sent to your clinician
      </StatusPill>
    )}
  </PatientStep>
);

export const CardUnavailableView = () => (
  <PatientStep current={0} center>
    <Headline>Card calibration is not available on this screen size.</Headline>
    <Muted>Please tell your clinician. They can ask you to measure your screen with a ruler or to select your device from the list.</Muted>
  </PatientStep>
);

// ---------------------------------------------------------------------------------------------
// Ruler measurement (patient)
// ---------------------------------------------------------------------------------------------
export const RulerView = ({ widthMm, heightMm, onWidth, onHeight, onSubmit, submitted, canSubmit }) => (
  <PatientStep current={0}>
    <Headline>Screen Measurement Instructions</Headline>
    <Steps>
      <li>Use a ruler or a tape measure to measure your screen, without the edge (bezel).</li>
      <li>Enter the width and the height in millimetres below.</li>
      <li>Tap ‘Submit’ when you are ready to continue.</li>
    </Steps>
    <FieldRow>
      <Field label='Width (mm)' type='number' inputMode='numeric' placeholder='--mm' value={widthMm} onChange={(e) => onWidth(e.target.value)} name='input-device-width' />
      <Field label='Height (mm)' type='number' inputMode='numeric' placeholder='--mm' value={heightMm} onChange={(e) => onHeight(e.target.value)} name='input-device-height' />
    </FieldRow>
    {submitted && (
      <StatusPill tone='success'>
        <CheckIcon size={14} strokeWidth={3} /> Values sent to your clinician
      </StatusPill>
    )}
    <SessionActions primary={{ label: 'Submit', onClick: onSubmit, disabled: !canSubmit }} />
  </PatientStep>
);

// ---------------------------------------------------------------------------------------------
// Camera calibration (patient holds the IBIS tool)
// ---------------------------------------------------------------------------------------------
export const CameraToolView = ({ calibrating, blindspot }) => (
  <PatientStep current={0} done={[]}>
    <Headline style={{ marginTop: 12 }}>
      {blindspot ? 'Please sit 50cm from your screen and look straight at the camera.' : 'Position the tool and ask the clinician to start the calibration.'}
    </Headline>
    <Muted>Keep still while your clinician calibrates your camera. This only takes a few seconds.</Muted>
    {calibrating ? <StatusPill tone='warning'>Calibrating…</StatusPill> : <StatusPill tone='neutral'>Waiting for your clinician</StatusPill>}
  </PatientStep>
);

// ---------------------------------------------------------------------------------------------
// P5 / P6  Distance detection / collected
// ---------------------------------------------------------------------------------------------
export const DistanceView = ({ phase = 'waiting', counter, distanceMm }) => {
  const collected = phase === 'collected';
  return (
    <PatientStep current={collected ? 2 : 1}>
      <Headline style={{ marginTop: 20 }} aria-live='polite'>
        {collected && (
          <>
            Your distance has now been collected.
            <br />
            Please await further instructions from your clinician.
          </>
        )}
        {phase === 'detecting' && (
          <>
            Your distance will now be detected.
            <br />
            Please hold still{counter > 0 ? ` for ${counter}.` : '.'}
          </>
        )}
        {phase === 'moving' && (
          <>
            A movement has been detected.
            <br />
            Please hold still{counter > 0 ? ` for ${counter}.` : '.'}
          </>
        )}
        {phase === 'waiting' && (
          <>
            Your distance from the screen will now be measured.
            <br />
            Please sit still and wait for your clinician.
          </>
        )}
      </Headline>
      <Field
        label='Collected Distance'
        accent={collected}
        readOnly
        style={collected ? filledField : undefined}
        value={collected && distanceMm ? `${Math.round(distanceMm)}mm` : '--mm'}
        data-testid='patient-collected-distance'
      />
    </PatientStep>
  );
};
